// DebitNoteList.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Card,
  Row,
  Col,
  Input,
  Button,
  Modal,
  ModalHeader,
  ModalBody,
  Spinner,
  Badge,
} from "reactstrap";
import { ImDownload } from "react-icons/im";
import { FaPlus } from "react-icons/fa";
import swal from "sweetalert";
import jsPDF from "jspdf";
import "jspdf-autotable";
import * as XLSX from "xlsx";

/** Use your shared imports from ../import */
import SuperAdminUI from "../import/SuperAdminUI";
import {
  DebitnoteOrderList,
  _PostSave,
} from "../import/ApiEndPoint/ApiCalling";
import { CheckPermission } from "../import/CheckPermission";
import { Create_Debit_Note } from "../import/ApiEndPoint/Api";
import AddNote from "./AddNote";

const allColumns = [
  { key: "uid", label: "UID", width: 70, center: true },
  { key: "CompanyName", label: "Name", width: 180 },
  { key: "email", label: "Email", width: 220 },
  { key: "mobileNumber", label: "Mobile Number", width: 160, center: true },
  { key: "createdAt", label: "Created At", width: 120, center: true },
  { key: "totalAmount", label: "Total Amount", width: 140, right: true },
];

function DebitNoteList() {
  const [loading, setLoading] = useState(false);
  const [masterShow, setMasterShow] = useState(false);
  const [permissions, setPermissions] = useState({});
  const [data, setData] = useState([]);
  const [q, setQ] = useState("");
  const [pageSize, setPageSize] = useState(15);
  const [page, setPage] = useState(1);
  const [dlOpen, setDlOpen] = useState(false);

  // add note modal
  const [addOpen, setAddOpen] = useState(false);

  // column visibility
  const [visibleCols, setVisibleCols] = useState(
    allColumns.reduce((m, c) => ({ ...m, [c.key]: true }), {})
  );

  useEffect(() => {
    const inside = CheckPermission("Debit Notes");
    setPermissions(inside || {});
    const user = JSON.parse(localStorage.getItem("userData") || "{}");
    if (user?.rolename?.roleName === "MASTER") setMasterShow(true);
    fetchData(user?._id, user?.database);
  }, []);

  async function fetchData(id, db) {
    if (!id || !db) return;
    setLoading(true);
    try {
      const res = await DebitnoteOrderList(db, id);
      const list = (res?.DebitNote || []).reverse();
      setData(list);
    } catch (e) {
      setData([]);
      // optionally surface error
    } finally {
      setLoading(false);
    }
  }

  // SuperAdmin switch
  const handleMasterSubmit = (e) => {
    e.preventDefault();
    const sel = JSON.parse(
      localStorage.getItem("SuperadminIdByMaster") || "null"
    );
    if (!sel) return;
    const [id, db] = sel.split(" ");
    fetchData(id, db);
  };
  const handleMasterChange = (val) => {
    localStorage.setItem("SuperadminIdByMaster", JSON.stringify(val));
  };

  // build flat row for table & export
  const cooked = useMemo(() => {
    return (data || []).map((r, i) => ({
      uid: i + 1,
      CompanyName: r?.partyId?.CompanyName || "",
      email: r?.partyId?.email || "",
      mobileNumber: r?.partyId?.mobileNumber || r?.partyId?.contactNumber || "",
      createdAt: (r?.createdAt || "").split("T")[0] || "",
      totalAmount:
        typeof r?.totalAmount === "number"
          ? r.totalAmount.toFixed(2)
          : (Number(r?.totalAmount) || 0).toFixed(2),
    }));
  }, [data]);

  // search filter
  const filtered = useMemo(() => {
    if (!q) return cooked;
    const term = q.toLowerCase();
    return cooked.filter((row) =>
      Object.values(row).join(" ").toLowerCase().includes(term)
    );
  }, [q, cooked]);

  // pagination
  const total = filtered.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const curPage = Math.min(page, totalPages);
  const paged = useMemo(() => {
    const start = (curPage - 1) * pageSize;
    return filtered.slice(start, start + pageSize);
  }, [filtered, curPage, pageSize]);

  // exports (respect visible columns)
  const visibleColumnList = allColumns.filter((c) => visibleCols[c.key]);
  const exportHeaders = visibleColumnList.map((c) => c.label);
  const exportRows = filtered.map((r) =>
    visibleColumnList.map((c) => r[c.key])
  );

  const exportCSV = () => {
    const rows = [exportHeaders, ...exportRows];
    const csv = rows.map((r) => r.map(escapeCSV).join(",")).join("\n");
    downloadBlob(
      new Blob([csv], { type: "text/csv;charset=utf-8;" }),
      "DebitNote.csv"
    );
  };

  const exportXLS = () => {
    const aoa = [exportHeaders, ...exportRows];
    const ws = XLSX.utils.aoa_to_sheet(aoa);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "DebitNote");
    XLSX.writeFile(wb, "DebitNote.xlsx");
  };

  const exportPDF = () => {
    const doc = new jsPDF("landscape", "mm", "a4");
    doc.setFontSize(12);
    doc.text("Debit Note List", 10, 10);
    doc.autoTable({
      head: [exportHeaders],
      body: exportRows,
      startY: 14,
      styles: { fontSize: 8 },
      headStyles: { fillColor: [240, 240, 240] },
    });
    doc.save("DebitNote.pdf");
  };

  // add note
  const handleAddSubmit = async (_e, form) => {
    try {
      await _PostSave(Create_Debit_Note, form);
      const user = JSON.parse(localStorage.getItem("userData") || "{}");
      await fetchData(user?._id, user?.database);
      setAddOpen(false);
      swal("success", "Added Successfully", "success");
    } catch (err) {
      swal("error", "Error Occurred", "error");
    }
  };

  return (
    <div style={{ padding: 6 }}>
      <StyleTag />

      <div className="crumb">
        <a href="/#/dashboard" className="crumb-home">
          Home
        </a>{" "}
        / Debit Notes
      </div>

      <Card className="card-slim">
        <Row className="toolbar">
          <Col className="title-wrap">
            <h2 className="title">Debit Note List</h2>
          </Col>

          {masterShow ? (
            <Col lg="3" md="4" sm="12" className="mb-1">
              <SuperAdminUI
                onDropdownChange={handleMasterChange}
                onSubmit={handleMasterSubmit}
              />
            </Col>
          ) : (
            <Col />
          )}

          <Col lg="3" md="4" sm="12" className="mb-1">
            <Input
              className="input-sm"
              placeholder="Search here..."
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                setPage(1);
              }}
            />
          </Col>

          <Col lg="1" md="2" sm="6" className="mb-1">
            <Button
              color="primary"
              className="btn-compact w-100"
              onClick={() => setAddOpen(true)}
              title="Add Debit Note"
            >
              <FaPlus size={12} />
              &nbsp;Add
            </Button>
          </Col>

          {permissions?.Download && (
            <Col lg="1" md="2" sm="6" className="mb-1">
              <div
                className="dl-wrap"
                onMouseEnter={() => setDlOpen(true)}
                onMouseLeave={() => setDlOpen(false)}
              >
                <Button color="secondary" className="btn-compact w-100">
                  <ImDownload size={14} />
                  &nbsp;Export
                </Button>
                {dlOpen && (
                  <div className="dl-menu">
                    <div className="dl-item" onClick={exportPDF}>
                      .PDF
                    </div>
                    <div className="dl-item" onClick={exportCSV}>
                      .CSV
                    </div>
                    <div className="dl-item" onClick={exportXLS}>
                      .XLSX
                    </div>
                  </div>
                )}
              </div>
            </Col>
          )}
        </Row>

        {/* Controls row */}
        <Row className="controls">
          <Col xs="6" sm="6" md="3">
            <label className="label-sm">Rows per page</label>
            <Input
              type="select"
              className="input-sm"
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setPage(1);
              }}
            >
              {[10, 15, 25, 50, 100].map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </Input>
          </Col>
          <Col xs="6" sm="6" md="3" className="right">
            <div className="muted">
              {total
                ? `${(curPage - 1) * pageSize + 1}–${Math.min(
                    curPage * pageSize,
                    total
                  )} of ${total}`
                : "0 of 0"}
            </div>
          </Col>
          <Col xs="12" md="6" className="right mt-sm-2">
            <Button
              className="btn-compact mr-1"
              color="light"
              disabled={curPage === 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Prev
            </Button>
            <Badge color="light" className="page-badge">
              {curPage}/{totalPages}
            </Badge>
            <Button
              className="btn-compact ml-1"
              color="light"
              disabled={curPage >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              Next
            </Button>
          </Col>
        </Row>

        {/* Table */}
        <Card className="table-card">
          {loading ? (
            <div className="loading">
              <Spinner size="sm" color="primary" />
              &nbsp;<span>Loading...</span>
            </div>
          ) : (
            <div className="table-scroll">
              <table className="table-mini">
                <thead>
                  <tr>
                    {visibleColumnList.map((c) => (
                      <th
                        key={c.key}
                        style={{ width: c.width }}
                        className={`${c.center ? "center" : ""} ${
                          c.right ? "right" : ""
                        }`}
                      >
                        {c.label}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {paged.length === 0 ? (
                    <tr>
                      <td
                        className="center muted"
                        colSpan={visibleColumnList.length}
                      >
                        No records
                      </td>
                    </tr>
                  ) : (
                    paged.map((r, i) => (
                      <tr key={`${r.uid}-${i}`}>
                        {visibleColumnList.map((c) => (
                          <td
                            key={c.key}
                            className={`${c.center ? "center" : ""} ${
                              c.right ? "right" : ""
                            }`}
                          >
                            {r[c.key]}
                          </td>
                        ))}
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </Card>
      </Card>

      {/* Add Debit Note */}
      <Modal
        isOpen={addOpen}
        toggle={() => setAddOpen(false)}
        size="md"
        backdrop={false}
      >
        <ModalHeader toggle={() => setAddOpen(false)}>
          Add Debit Note
        </ModalHeader>
        <ModalBody>
          <AddNote type="Debit" onSubmit={handleAddSubmit} />
        </ModalBody>
      </Modal>
    </div>
  );
}

/* Helpers */
function escapeCSV(val) {
  if (val == null) return "";
  const s = String(val);
  if (/[",\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}
function downloadBlob(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  URL.revokeObjectURL(url);
  a.remove();
}

/** Compact inline styles (“our design”, small fonts/boxes) */
function StyleTag() {
  return (
    <style>{`
      .crumb { font-size:12px; margin:4px 0 6px; }
      .crumb-home { color:#0a7; text-decoration:none; }
      .card-slim { border:1px solid #e5e7eb; border-radius:10px; padding:10px; }
      .title-wrap { display:flex; align-items:center; }
      .title { font-weight:600; text-transform:uppercase; font-size:16px; margin:6px 0; }
      .toolbar { align-items:center; margin:2px 4px 6px; }
      .controls { align-items:center; margin:0 4px 8px; }
      .muted { color:#6b7280; font-size:12px; }
      .right { text-align:right; }
      .center { text-align:center; }
      .label-sm { font-size:12px; font-weight:600; margin-bottom:4px; display:block; }
      .input-sm { height:32px; font-size:13px; }
      .btn-compact { height:30px; padding:0 12px; font-weight:600; font-size:12px; }
      .page-badge { font-size:12px; padding:6px 8px; }

      .dl-wrap { position:relative; display:inline-block; width:100%; }
      .dl-menu { position:absolute; top:34px; right:0; background:#fff; border:1px solid #e5e7eb; border-radius:8px; min-width:120px; z-index:9; box-shadow:0 8px 20px rgba(0,0,0,0.08); }
      .dl-item { padding:6px 10px; font-size:13px; cursor:pointer; }
      .dl-item:hover { background:#f3f4f6; }

      .table-card { border:1px solid #e5e7eb; border-radius:10px; }
      .table-scroll { overflow:auto; max-height:60vh; }
      .table-mini { width:100%; border-collapse:collapse; }
      .table-mini th, .table-mini td {
        border:1px solid #e5e7eb; padding:6px 8px; font-size:12.5px; white-space:nowrap;
      }
      .table-mini th { background:#f8fafc; font-weight:600; position:sticky; top:0; z-index:1; }

      .loading { display:flex; align-items:center; justify-content:center; height:180px; font-size:13px; }
    `}</style>
  );
}

export default DebitNoteList;
